import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import { useApolloClient } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';

import IdeaStatusAllocator from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusAllocator/ideaStatusAllocator';
import IdeaStatusContest from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusContest/ideaStatusContest';
import IdeaStatusDraft from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusDraft/ideaStatusDraft';
import IdeaStatusFlaggedForReview from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusFlaggedForReview/ideaStatusFlaggedForReview';
import IdeaStatusOpenAccess from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusOpenAccess/ideaStatusOpenAccess';
import IdeaStatusPaladinReview from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusPaladinReview/ideaStatusPaladinReview';
import IdeaStatusPendingPaladinApproval from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/IdeaStatusPendingPaladinApproval/ideaStatusPendingPaladinApproval';

import {
  IdeaCoreQuery,
  IdeaCurrentUserQuery,
  IdeaPositionsPricingDocument,
  IdeaPositionsPricingQuery
} from 'interfaces/graphql-typings';

const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');
const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');

import styles from './ideaStatusBarStyles.scss';

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const IdeaStatusBar: React.FC<{ ideaId: string; isAuthor: boolean }> = ({ ideaId, isAuthor }) => {
  const [closed, setClosed] = useState(false);

  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  const closeToTarget = client.readQuery<IdeaPositionsPricingQuery>({
    query: IdeaPositionsPricingDocument,
    variables: { ideaId }
  })?.idea.closeToTarget;

  if (closed) return null;

  const closeStatusBar = (): Promise<void> => delay(2000).then(() => setClosed(true));

  const {
    approved,
    closedAt,
    closeToTimeframe,
    pendingContestIdeas,
    direction,
    featuredOnAFundProfile,
    hasPendingEdit,
    isOpenAccess,
    isPairTrade,
    paladinReviews,
    pendingIntroRequest,
    status
  } = cachedIdea || {};
  const { id: currentUserId, isAdmin, isPaladin, isAllocator, canViewIdeas } =
    cachedCurrentUser || {};

  let component = null;

  if (approved === false) {
    component = <IdeaStatusFlaggedForReview ideaId={ideaId} paladinReviews={paladinReviews} />;
  } else if (status === 'draft') {
    component = <IdeaStatusDraft ideaId={ideaId} />;
  } else if (approved === null && isAuthor) {
    component = <IdeaStatusPendingPaladinApproval />;
  } else if (
    approved === null &&
    (isAdmin || isPaladin) &&
    !isEmpty(paladinReviews) &&
    currentUserId
  ) {
    component = (
      <IdeaStatusPaladinReview
        currentUserId={currentUserId}
        pendingContestIdeas={pendingContestIdeas}
        isAdmin={Boolean(isAdmin)}
        paladinReviews={paladinReviews}
      />
    );
  } else if (isAuthor && pendingContestIdeas && Boolean(pendingContestIdeas.length)) {
    component = <IdeaStatusContest isMultiple={pendingContestIdeas.length > 1} />;
  } else if (
    isAllocator &&
    featuredOnAFundProfile &&
    (!canViewIdeas || pendingIntroRequest?.status)
  ) {
    component = (
      <IdeaStatusAllocator
        closeBar={closeStatusBar}
        employerName={pendingIntroRequest?.employerName || null}
        pendingIntroRequest={pendingIntroRequest || null}
      />
    );
  } else if (isOpenAccess && !canViewIdeas) {
    component = <IdeaStatusOpenAccess />;
  } else if (isAuthor && status === 'open' && closeToTimeframe) {
    component = (
      <div className={styles.authorNotification}>
        <FontAwesomeIcon className={styles.faIcon} icon={faExclamationCircle} />
        Your idea is close to the end of its timeframe. Extend your idea's timeframe to avoid
        auto-closure by adding an update.
      </div>
    );
  } else if (isAuthor && status === 'open' && closeToTarget) {
    component = (
      <div className={styles.authorNotification}>
        <FontAwesomeIcon className={styles.faIcon} icon={faExclamationCircle} />
        Your idea is close to hitting its {isPairTrade ? 'return' : 'price'} target.{' '}
        {direction === 'long' ? 'Increase' : 'Decrease'} your idea's price target to avoid
        auto-closure by adding an update.
      </div>
    );
  } else if (isAuthor && hasPendingEdit) {
    component = (
      <div className={styles.authorNotification}>
        <FontAwesomeIcon className={styles.faIcon} icon={faExclamationCircle} />
        Your edits to the original thesis are currently under review. You will be notified if it is
        approved by SumZero admins.
      </div>
    );
  }

  return (
    <>
      {component}
      {closedAt && <div className={styles.positionClosed}>Position Closed</div>}
    </>
  );
};

export default IdeaStatusBar;
